import {useCallback, useState, useMemo} from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    Platform,
} from 'react-native';
import Alert from "@/lib/Alert";
import {Image} from 'expo-image';
import {useTranslation} from 'react-i18next';
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {useOrganizationOffers, useCreateOfferCheckout, useCanPurchase} from '@/data/offer-hooks';
import {HeaderText} from "@/components/common/HeaderText";
import {ThemeType} from "@/theming/baseTheme";
import {useOrganizationNavigation} from "@/hooks/useOrganizationNavigation";
import {useHardwareBack} from "@/hooks/useHardwareBack";
import {OfferPriceDisplay} from './OfferPriceDisplay';
import {OfferPurchaseButton} from './OfferPurchaseButton';
import {PaymentProviderModal} from './PaymentProviderModal';
import {OfferMediaSection} from './OfferMediaSection';
import {OfferAchievementsSection} from './OfferAchievementsSection';
import {useInvalidator} from "@/data/app-data-hooks";
import {Ionicons} from '@expo/vector-icons';

interface OfferDetailScreenProps {
    orgId: string;
    offerId: string;
}

export const OfferDetailScreen = ({orgId, offerId}: OfferDetailScreenProps) => {
    const {t} = useTranslation();
    const {theme} = useCurrentTheme();
    const {data: offers} = useOrganizationOffers(orgId);
    const createCheckout = useCreateOfferCheckout();
    const canPurchase = useCanPurchase();
    const [isProcessing, setIsProcessing] = useState(false);
    const [showProviderSelection, setShowProviderSelection] = useState(false);
    const [selectedProvider, setSelectedProvider] = useState<string | undefined>();
    const navigation = useOrganizationNavigation(orgId);
    const {invalidateAppData} = useInvalidator();

    const styles = createStyles(theme);

    // Find the specific offer
    const offer = useMemo(() => {
        return offers?.find(o => o.id === offerId);
    }, [offers, offerId]);

    // Check if already purchased (from offer data)
    const isPurchased = offer?.isPurchased || false;

    const blurhash = offer?.imageBlurhash && offer.imageBlurhash !== 'undefined'
        ? offer.imageBlurhash
        : 'LKO2?U%2Tw=w]~RBVZRi};RPxuwH';

    // Get price info from offer
    const priceInfo = useMemo(() => {
        if (!offer) return {price: 0, currency: 'EUR', isFree: true};

        return {
            price: offer.price ? offer.price / 100 : 0,
            currency: offer.currency || 'EUR',
            isFree: !offer.price || offer.price === 0,
        };
    }, [offer]);

    const handleBack = useCallback(() => {
        navigation.navigateBack();
    }, [navigation]);

    const handlePurchase = useCallback(async () => {
        if (!offer || isPurchased || isProcessing) return;

        // For paid offers, check if provider selection is needed
        if (!priceInfo.isFree && offer.availablePaymentProviders) {
            setShowProviderSelection(true);
            return;
        }

        // Determine payment provider
        const paymentProvider = priceInfo.isFree
            ? undefined
            : selectedProvider || offer.availablePaymentProviders?.[0];

        setIsProcessing(true);

        try {
            const result = await createCheckout.mutateAsync({
                offerId: offer.id,
                paymentProvider,
            });

            if (result.checkoutUrl) {
                // Open checkout URL in browser (web will handle differently)
                if (Platform.OS === 'web') {
                    window.location.href = result.checkoutUrl;
                } else {
                    // This shouldn't happen on mobile for paid offers
                    Alert.alert(t('offers.errors.general'), t('offers.errors.webOnly'));
                }
            } else if (result.success) {
                // Free offer claimed successfully
                Alert.alert(t('common.success'), result.message || t('offers.success.claimed'));
                invalidateAppData();
                // Refresh purchases
                navigation.navigateBack();
            }
        } catch (error: any) {
            Alert.alert(t('offers.errors.general'), error.message || t('offers.errors.purchaseFailed'));
        } finally {
            setIsProcessing(false);
            setShowProviderSelection(false);
        }
    }, [t, offer, isPurchased, isProcessing, createCheckout, priceInfo, navigation, selectedProvider]);

    const handleProviderSelect = useCallback(async (providerId: string) => {
        setSelectedProvider(providerId);
        setShowProviderSelection(false);

        // Proceed with purchase using selected provider
        if (!offer || isPurchased || isProcessing) return;

        setIsProcessing(true);

        try {
            const result = await createCheckout.mutateAsync({
                offerId: offer.id,
                paymentProvider: providerId,
            });

            if (result.checkoutUrl) {
                if (Platform.OS === 'web') {
                    window.location.href = result.checkoutUrl;
                } else {
                    setIsProcessing(false);
                    Alert.alert(t('offers.errors.general'), t('offers.errors.webOnly'));
                }
            } else if (result.success) {
                Alert.alert(t('common.success'), result.message || t('offers.success.claimed'));
                setIsProcessing(false);
                navigation.navigateBack();
            }
        } catch (error: any) {
            Alert.alert(t('offers.errors.general'), error.message || t('offers.errors.purchaseFailed'));
            setIsProcessing(false);
        }
    }, [t, offer, isPurchased, isProcessing, createCheckout, navigation]);

    useHardwareBack(() => {
        handleBack();
        return true;
    });

    if (!offer) {
        return (
            <View style={styles.container}>
                <HeaderText
                    title={t('offers.notFound')}
                    onBackPress={handleBack}
                />
                <View style={styles.errorContainer}>
                    <Text style={styles.errorText}>{t('offers.notFoundMessage')}</Text>
                </View>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <HeaderText
                title={t('offers.detailsTitle')}
                onBackPress={handleBack}
            />

            <ScrollView
                style={styles.scrollView}
            >
                {offer.image?.lg && (
                    <Image
                        source={{uri: offer.image.lg}}
                        placeholder={{blurhash}}
                        style={styles.heroImage}
                        contentFit="cover"
                        transition={200}
                    />
                )}

                <View style={styles.contentContainer}>
                    <Text style={styles.title}>{offer.title}</Text>
                    <Text style={styles.description}>{offer.description}</Text>

                    {/* Content badges */}
                    <View style={styles.badgesContainer}>
                        {offer.mediaItemCount?.video > 0 && (
                            <View style={[styles.badge, {backgroundColor: theme.colors.secondary + '20'}]}>
                                <Ionicons name="videocam" size={14} color={theme.colors.secondary} />
                                <Text style={[styles.badgeText, {color: theme.colors.secondary}]}>
                                    {t('offers.videoCount', {count: offer.mediaItemCount.video})}
                                </Text>
                            </View>
                        )}
                        {(offer.mediaItemCount?.audio ?? 0) > 0 && (
                            <View style={[styles.badge, {backgroundColor: theme.colors.secondary + '20'}]}>
                                <Ionicons name="musical-notes" size={14} color={theme.colors.secondary} />
                                <Text style={[styles.badgeText, {color: theme.colors.secondary}]}>
                                    {t('offers.audioCount', {count: offer.mediaItemCount!.audio})}
                                </Text>
                            </View>
                        )}
                        {offer.mediaItemCount?.pdf > 0 && (
                            <View style={[styles.badge, {backgroundColor: theme.colors.secondary + '20'}]}>
                                <Ionicons name="document-text" size={14} color={theme.colors.secondary} />
                                <Text style={[styles.badgeText, {color: theme.colors.secondary}]}>
                                    {t('offers.pdfCount', {count: offer.mediaItemCount.pdf})}
                                </Text>
                            </View>
                        )}
                        {(offer.mediaItemCount?.document ?? 0) > 0 && (
                            <View style={[styles.badge, {backgroundColor: theme.colors.secondary + '20'}]}>
                                <Ionicons name="document-attach" size={14} color={theme.colors.secondary} />
                                <Text style={[styles.badgeText, {color: theme.colors.secondary}]}>
                                    {t('offers.documentCount', {count: offer.mediaItemCount!.document})}
                                </Text>
                            </View>
                        )}
                        {(offer.mediaItemCount?.image ?? 0) > 0 && (
                            <View style={[styles.badge, {backgroundColor: theme.colors.secondary + '20'}]}>
                                <Ionicons name="image" size={14} color={theme.colors.secondary} />
                                <Text style={[styles.badgeText, {color: theme.colors.secondary}]}>
                                    {t('offers.imageCount', {count: offer.mediaItemCount!.image})}
                                </Text>
                            </View>
                        )}
                        {(offer.includedAchievements?.length || 0) > 0 && (
                            <View style={[styles.badge, {backgroundColor: theme.colors.secondary + '20'}]}>
                                <Ionicons name="trophy" size={14} color={theme.colors.secondary} />
                                <Text style={[styles.badgeText, {color: theme.colors.secondary}]}>
                                    {t('offers.achievementCount', {count: offer.includedAchievements?.length || 0})}
                                </Text>
                            </View>
                        )}
                    </View>

                    {/* Show achievement requirement if needed */}
                    {offer.hasRequiredAchievements === false && (
                        <View style={styles.section}>
                            <Text style={styles.warningText}>
                                {t('offers.missingAchievements', {missing: offer.missingAchievements?.join(", ") || ''})}
                            </Text>
                        </View>
                    )}

                    <View style={styles.priceSection}>
                        <OfferPriceDisplay
                            priceInfo={priceInfo}
                            isPurchased={isPurchased}
                            taxBreakdown={offer.taxBreakdown}
                            paymentDenied={offer.paymentDenied}
                        />

                        {!isPurchased && !offer.paymentDenied && (
                            <OfferPurchaseButton
                                priceInfo={priceInfo}
                                canPurchase={canPurchase}
                                isProcessing={isProcessing}
                                hasRequiredAchievements={offer.hasRequiredAchievements !== false}
                                onPurchase={handlePurchase}
                            />
                        )}
                    </View>
                    {/* Included Media Section */}
                    <OfferMediaSection mediaItems={offer.includedMedia || []} />

                    {/* Included Achievements Section */}
                    <OfferAchievementsSection achievements={offer.includedAchievements || []} />

                </View>
            </ScrollView>

            {/* Payment Provider Selection Modal */}
            <PaymentProviderModal
                visible={showProviderSelection}
                availableProviders={offer?.availablePaymentProviders || []}
                isProcessing={isProcessing}
                onSelectProvider={handleProviderSelect}
                onClose={() => setShowProviderSelection(false)}
            />
        </View>
    );
};

const createStyles = (theme: ThemeType) => StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background
    },
    scrollView: {
        flex: 1,
    },
    heroImage: {
        width: '100%',
        aspectRatio: 16 / 9,
        backgroundColor: theme.colors.surface,
    },
    contentContainer: {
        padding: 20,
    },
    errorContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    errorText: {
        fontSize: 16,
        color: theme.colors.textSecondary,
        textAlign: 'center',
    },
    title: {
        fontSize: 24,
        fontWeight: 'bold',
        color: theme.colors.text,
        marginBottom: 12,
    },
    description: {
        fontSize: 16,
        color: theme.colors.textSecondary,
        lineHeight: 24,
        marginBottom: 24,
    },
    section: {
        marginVertical: 20,
    },
    warningText: {
        fontSize: 14,
        color: theme.colors.error || theme.colors.text,
        fontWeight: '600',
        textAlign: 'center',
    },
    badgesContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        marginBottom: 24,
    },
    badge: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 12,
        gap: 4,
    },
    badgeText: {
        fontSize: 12,
        fontWeight: '500',
    },
    priceSection: {
        marginVertical: 24,
        padding: 24,
        backgroundColor: theme.colors.surface,
        borderRadius: 16,
        borderWidth: 1,
        borderColor: theme.colors.border,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.05,
        shadowRadius: 8,
        elevation: 2,
    },
});
